import clsx from 'clsx';
import React from 'react';

interface Benefit {
  title: string;
  description: string;
  icon: string;
  alt: string;
}

const benefitsData: Benefit[] = [
  {
    title: 'Centralized Access',
    description: 'Manage processes, documents, and communication—all in one place.',
    icon: '/images/centralized-access.png',
    alt: 'Centralized Access Icon',
  },
  {
    title: 'Real-Time Visibility',
    description: 'Track deadlines, updates, and key financial tasks in real time.',
    icon: '/images/realtime-visibility.png',
    alt: 'Real-Time Visibility Icon',
  },
  {
    title: 'Improved Collaboration',
    description: 'Easily communicate with your CloudCFO team and stakeholders.',
    icon: '/images/improved-collaboration.png',
    alt: 'Improved Collaboration Icon',
  },
  {
    title: 'Secure Data Management',
    description: 'Access and store important financial files securely, anytime you need them.',
    icon: '/images/secured-data-mgmt.png',
    alt: 'Secure Data Management Icon',
  },
  {
    title: 'Streamlined Workflows',
    description: 'Stay organized with processes aligned to your financial calendar.',
    icon: '/images/streamline-workflows.png',
    alt: 'Streamlined Workflows Icon',
  },
  {
    title: 'Tech-Powered Efficiency',
    description: 'Automated tools for faster, more accurate results.',
    icon: '/images/tech-powered-efficiency.png',
    alt: 'Tech-Powered Efficiency Icon',
  },
];

const BenefitsSection: React.FC = () => {
  return (
    <section
      className={clsx(
        'tw-bg-gradient-to-b',
        'from-blue-600',
        'to-blue-700',
        'tw-py-12',
        'lg:tw-py-24',
        'tw-relative'
      )}
      style={{
        backgroundImage: `url('images/blue-background.png')`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
      }}
    >
      <div
        className={clsx('tw-container', 'tw-mx-auto', 'tw-px-6', 'lg:tw-px-8', 'tw-text-center')}
      >
        <h2
          className={clsx(
            'tw-text-white',
            'tw-text-2xl',
            'sm:tw-text-3xl',
            'lg:tw-text-4xl',
            'tw-font-extrabold',
            'tw-mb-8',
            'md:tw-pb-10',
            'md:tw-max-w-[20%]',
            'tw-max-w-full',
            'tw-leading-snug',
            'tw-justify-self-center'
          )}
        >
          {`Why You'll Love The CloudCFO WebApp`}
        </h2>
        <div
          className={clsx(
            'tw-grid',
            'tw-grid-cols-1',
            'md:tw-grid-cols-2',
            'lg:tw-grid-cols-3',
            'tw-gap-6',
            'md:tw-gap-8',
            'lg:tw-gap-10',
            'tw-px-8',
            'md:tw-px-2'
          )}
        >
          {benefitsData.map((benefit, index) => (
            <div
              key={index}
              className={clsx(
                'tw-bg-white',
                'tw-rounded-xl',
                'tw-shadow-lg',
                'tw-p-6',
                'tw-flex',
                'tw-items-start', // Align items to the start for the two-column layout
                'tw-text-left' // Align text to the left within the columns
              )}
            >
              {/* First Column: Image */}
              <div className={clsx('tw-w-20', 'tw-h-20', 'tw-mr-4', 'tw-flex-shrink-0')}>
                <img
                  src={benefit.icon}
                  alt={benefit.alt}
                  className={clsx('tw-w-full', 'tw-h-full', 'tw-object-contain')}
                />
              </div>
              {/* Second Column: Title and Text */}
              <div>
                <h3
                  className={clsx(
                    'tw-text-lg',
                    'tw-font-extrabold',
                    'tw-mb-1',
                    'tw-text-black',
                    'lg:tw-text-[14px]'
                  )}
                >
                  {benefit.title}
                </h3>
                <p
                  className={clsx(
                    'tw-text-gray-700',
                    'tw-text-sm',
                    'lg:tw-text-[12px]',
                    'lg:tw-leading-snug'
                  )}
                >
                  {benefit.description}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
      {/* Gradient Divider */}
      <div
        className={clsx(
          'tw-absolute',
          'tw-bottom-0',
          'tw-left-0',
          'tw-w-full',
          'tw-h-6',
          'bg-gradient-to-r',
          'from-blue-400',
          'to-purple-500'
        )}
      />
    </section>
  );
};

export default BenefitsSection;
